import { Component, inject, Input } from '@angular/core';
import { ListView } from 'modules/list-view';
import { QueryInputComponent } from './query-input.component';
import { Inputs } from 'modules/inputs';
import { Pipes } from 'modules/pipes';
import { InputType } from 'enums/InputType';
import { QueryType } from 'enums/QueryType';
import { DynamicQueryModel } from 'models/generated/Models.DynamicQuery';
import { Comparison, ExpressionFilterModel } from 'models/generated/Models.ExpressionFilter';
import { QueryPropertyModel, QueryPropertyType } from 'models/generated/Models.QueryProperty';
import { TranslatePipe } from 'pipes/translate.pipe';
import { FilterValue, InlineQuery, QueryService } from 'services/query.service';
import { QueryInputOptionsService } from './query-input-options.service';

/** A copy of one of the filters of the query being executed, holding the value it is executed with. */
interface IInputFilter extends ExpressionFilterModel {
	/** Whether the user fills in the value: the filter was saved without one. Other filters keep their saved value. */
	isInput: boolean;
	inputValue: FilterValue;
	/** Set when the user chose to execute with an input left empty, which the backend treats as "is empty". */
	allowEmpty?: boolean;
	/** The sub-query a Query filter's value points to (a saved query or an inline one), with its own inputs. */
	subQuery?: IInputQuery;
}

/** The query being executed or one of its sub-queries, with its filters copied so the inputs can be filled without changing the query. */
interface IInputQuery {
	name: string;
	queryType: QueryType;
	queryProperties: QueryPropertyModel[];
	filters: IInputFilter[];
	/** The ids of the saved queries this one is reached through, including its own, to stop at reference cycles. */
	savedQueryPath: string[];
}

/**
 * Lists the inputs of a query, including those of its sub-queries, so the user can fill them in before it is executed.
 * The query itself is never changed: getExecutableQuery returns a copy with the values filled in and every sub-query inlined.
 */
@Component({
	selector: 'query-inputs',
	templateUrl: './query-inputs.component.html',
	imports: [QueryInputComponent, ListView, Inputs, Pipes],
	// The inputs share the options for their dropdowns, which are requested again for each list of inputs
	providers: [QueryInputOptionsService]
})
export class QueryInputsComponent {

	inputTypeEnum = InputType;
	operatorType = Comparison;

	inputs: { query: IInputQuery, filter: IInputFilter }[] = [];

	private _query?: DynamicQueryModel;
	private root?: IInputQuery;
	private loading: Promise<void> = Promise.resolve();
	private savedQueries = new Map<string, Promise<IInputQuery | undefined>>();

	/** The name of the query, shown for its own inputs. Inline sub-queries are named after it. */
	@Input() name = '';

	/** A copy of the query returned by getQueryWithInputs earlier, e.g. stored with a list. Its values pre-fill the inputs. */
	@Input() filledQuery?: DynamicQueryModel;

	/** The query to execute. It may reference saved queries by id and contain inline sub-queries, but not unsaved { queryRef } references. */
	@Input()
	set query(query: DynamicQueryModel | undefined) {
		this._query = query;
		this.loading = this.load(query);
	}
	get query() {
		return this._query;
	}

	private readonly queryService = inject(QueryService);
	private readonly translate = inject(TranslatePipe);

	/** Whether every input has a value, or is explicitly left empty where that is allowed. */
	get isComplete() {
		return this.inputs.every(({ filter }) => !QueryInputsComponent.isEmpty(filter.inputValue) || (filter.allowEmpty && this.canBeEmpty(filter)));
	}

	/** A copy of the query with the inputs filled in and every sub-query inlined, or undefined if an input is missing. */
	async getExecutableQuery(): Promise<DynamicQueryModel | undefined> {
		await this.loading;
		return this.isComplete ? this.getQueryWithInputs() : undefined;
	}

	/** Like getExecutableQuery, but also when inputs are missing, e.g. to store the values filled in so far. */
	async getQueryWithInputs(): Promise<DynamicQueryModel | undefined> {
		await this.loading;
		if (!this._query || !this.root) return undefined;
		const queryData = QueryService.cloneQueryData(this._query);
		// The root's filters are copies of the query's filters that have a property, in the same order
		queryData.filters = queryData.filters.filter(filter => filter.property);
		queryData.filters.forEach((filter, index) => filter.value = this.getExecutionValue(this.root!.filters[index]));
		return queryData;
	}

	/** Whether an input may be executed without a value, which the backend treats as "is empty". */
	canBeEmpty(filter: IInputFilter) {
		return !!filter.property?.nullable && (filter.operator == Comparison.Equal || filter.operator == Comparison.NotEqual);
	}

	getPropertyLabel(query: IInputQuery, filter: IInputFilter) {
		return this.translate.transform('query.' + this.queryService.getPropertyLanguageString(filter.property?.name ?? '', QueryType[query.queryType]));
	}

	setInputValue(query: IInputQuery, filter: IInputFilter, value: FilterValue) {
		filter.inputValue = value;
		if (filter.property?.type != QueryPropertyType.Query) return;
		// Choosing a sub-query brings in its inputs
		this.loading = this.loading.then(async () => {
			filter.subQuery = await this.loadSubQuery(query, filter);
			this.updateInputs();
		});
	}

	private async load(query: DynamicQueryModel | undefined) {
		this.root = undefined;
		this.inputs = [];
		this.savedQueries.clear();
		if (!query) return;
		const root = await this.createInputQuery(this.name, query, []);
		// Ignore the result if the query was replaced while loading
		if (query != this._query) return;
		if (this.filledQuery) await this.fillInputs(root, this.filledQuery.filters);
		this.root = root;
		this.updateInputs();
	}

	/** Copies the input values from a filled copy of the query, matching its filters by position, and does the same in its inline sub-queries. */
	private async fillInputs(query: IInputQuery, filledFilters: { property?: { name: string }, value?: FilterValue }[]) {
		const filters = filledFilters.filter(filter => filter.property);
		for (const [index, filter] of query.filters.entries()) {
			const filledFilter = filters[index];
			if (filledFilter?.property?.name?.toLowerCase() != filter.property?.name?.toLowerCase()) continue;
			if (filter.isInput) {
				filter.inputValue = filledFilter.value;
				filter.subQuery = await this.loadSubQuery(query, filter);
			} else if (filter.subQuery && QueryService.isInlineQuery(filledFilter.value)) {
				await this.fillInputs(filter.subQuery, filledFilter.value.filters);
			}
		}
	}

	private async createInputQuery(name: string, data: DynamicQueryModel | InlineQuery, savedQueryPath: string[]): Promise<IInputQuery> {
		const { queryData, queryProperties } = await this.queryService.loadQueryData(data);
		const filters = queryData.filters
			.filter(filter => filter.property)
			.map(filter => Object.assign(filter, { isInput: QueryInputsComponent.isInputFilter(filter), inputValue: filter.value as FilterValue }) as IInputFilter);
		const inputQuery: IInputQuery = { name, queryType: queryData.queryType, queryProperties, filters, savedQueryPath };
		for (const filter of filters) filter.subQuery = await this.loadSubQuery(inputQuery, filter);
		return inputQuery;
	}

	private async loadSubQuery(parent: IInputQuery, filter: IInputFilter): Promise<IInputQuery | undefined> {
		if (filter.property?.type != QueryPropertyType.Query) return undefined;
		const value = filter.inputValue;
		if (QueryService.isInlineQuery(value)) return this.createInputQuery(`${parent.name} › ${this.getPropertyLabel(parent, filter)}`, value, parent.savedQueryPath);
		// A cyclic reference is executed as it is and left to the server to reject
		if (typeof value != 'string' || value == '' || parent.savedQueryPath.includes(value)) return undefined;
		// A saved query referenced more than once gets one set of inputs
		if (!this.savedQueries.has(value)) this.savedQueries.set(value, this.loadSavedQuery(value, parent.savedQueryPath));
		return this.savedQueries.get(value);
	}

	private async loadSavedQuery(id: string, savedQueryPath: string[]): Promise<IInputQuery | undefined> {
		const savedQuery = await this.queryService.getQuery(id).catch(() => undefined);
		if (!savedQuery?.data) return undefined;
		return this.createInputQuery(savedQuery.name, JSON.parse(savedQuery.data) as DynamicQueryModel, [...savedQueryPath, id]);
	}

	/** Lists the inputs of the query and its sub-queries, each sub-query once even if it is referenced more than once. */
	private updateInputs() {
		const queries = new Set<IInputQuery>();
		const visit = (query: IInputQuery) => {
			if (queries.has(query)) return;
			queries.add(query);
			query.filters.forEach(filter => filter.subQuery && visit(filter.subQuery));
		};
		if (this.root) visit(this.root);
		this.inputs = [...queries].flatMap(query => query.filters.filter(filter => filter.isInput).map(filter => ({ query, filter })));
	}

	private getExecutionValue(filter: IInputFilter): FilterValue {
		const subQuery = filter.subQuery;
		if (!subQuery) return filter.inputValue;
		return QueryService.toInlineQuery(subQuery.queryType, subQuery.filters, subQueryFilter => this.getExecutionValue(subQueryFilter));
	}

	/** Whether the filter is an input: any filter saved without a value, except "is empty"/"has a value" filters. */
	private static isInputFilter(filter: ExpressionFilterModel) {
		return QueryInputsComponent.isEmpty(filter.value) && filter.operator != Comparison.Null && filter.operator != Comparison.NotNull;
	}

	private static isEmpty(value: FilterValue) {
		return value == null || value === '';
	}
}
